<template>
<div class="duration-timeline-v2">
  <!-- Outliers count lives in its own row so it never shifts or affects the
       transition row's alignment with the funnel nodes above. -->
  <div class="duration-outliers-row-v2">
    <span class="count-number-v2">{{ overDue.total }}</span>
    <span class="count-label-v2">Outliers</span>
  </div>

  <!-- One pill per stage transition. The leading/trailing half-width spacers make
       the row behave like (transitions + 1) equal funnel cells, so each pill — and
       therefore its duration badge, label and count — centers exactly between its
       two funnel nodes above (Sold|Activated, Activated|Submitted, …) at ANY width.
       Each pill stacks: average-duration badge → transition label → count. -->
  <div class="duration-pills-scroll-v2">
    <div class="duration-pills-v2">
      <span class="pill-spacer-v2" aria-hidden="true"></span>
      <div
        class="duration-pill-v2"
        v-for="pill in pills"
        :key="pill.key"
        @click="ShowOutlinerKits(pill.statusCode, pill.value)"
      >
        <div class="stage-pill-v2" :class="pill.pillClass">
          <span>{{ avgDays(kitAvg[pill.avgKey]) }}</span>
        </div>
        <div class="pill-stage-label-v2">{{ pill.label }}</div>
        <div class="pill-outlier-count-v2">
          <span :class="pill.spanClass">{{ overDue[pill.countKey] || 0 }}</span>
        </div>
      </div>
      <span class="pill-spacer-v2" aria-hidden="true"></span>
    </div>
  </div>

  <div v-if="overDue.last_run_date" class="last-run-date-v2">
    Last update on: {{ formatDate(overDue.last_run_date) }}
  </div>
</div>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import moment from "moment";

export default {
  name: "DurationTimelineV2",
  data() {
    return {
      overDue: {
        ordered_count: 0,
        shipped_count: 0,
        delivered_count: 0,
        activated_count: 0,
        awaiting_count: 0,
        received_count: 0,
        accepted_count: 0,
        dna_count: 0,
        sequencing_count: 0,
        completed_total: 0,
        total: 0,
        last_run_date: ""
      },
      kitAvg: {}
    };
  },
  computed: {
    ...mapGetters(["getOverDuesV2", "getKitAvgV2"]),
    pills() {
      // statusCode/value drive the outlier filter; avgKey/countKey map to the
      // overdue + averages payloads returned by /kitsv2/overdues.
      // One pill per consecutive Kit V2 stage transition. The count on each pill
      // is the overdue (outlier) count of the transition's source stage, so the
      // count and the average duration always belong to the same stage hand-off.
      // One pill per consecutive Kit V2 stage transition, in the new lab-workflow
      // order: Sold → Activated → Submitted → DNA Extraction → Sequencing →
      // Lab Received → Completed. The lab drives DNA Extraction and Sequencing
      // during processing (before Lab Received, the terminal lab stage on FASTQ
      // upload), so the lab hand-offs are Submitted → DNA (awaiting_to_dna),
      // DNA → Sequencing, Sequencing → Lab Received (sequencing_to_received), and
      // Lab Received → Completed (received_to_completed).
      return [
        { key: "shipped", statusCode: 0, value: "shipped", label: "Sold → Activated", avgKey: "shipped_to_activated", countKey: "shipped_count", pillClass: "shipping-v2", spanClass: "shipping-span-v2" },
        { key: "activated", statusCode: 1, value: "activated", label: "Activated → Submitted", avgKey: "activated_to_awaiting", countKey: "activated_count", pillClass: "activation-v2", spanClass: "activation-span-v2" },
        { key: "awaiting", statusCode: 2, value: "awaiting", label: "Submitted → DNA Extraction", avgKey: "awaiting_to_dna", countKey: "awaiting_count", pillClass: "awaiting-v2", spanClass: "awaiting-span-v2" },
        { key: "dna_extraction", statusCode: 13, value: "dna_extraction", label: "DNA Extraction → Sequencing", avgKey: "dna_to_sequencing", countKey: "dna_count", pillClass: "dna-v2", spanClass: "dna-span-v2" },
        { key: "sequencing", statusCode: 14, value: "sequencing", label: "Sequencing → Lab Received", avgKey: "sequencing_to_received", countKey: "sequencing_count", pillClass: "sequencing-v2", spanClass: "sequencing-span-v2" },
        { key: "received", statusCode: 3, value: "received", label: "Lab Received → Completed", avgKey: "received_to_completed", countKey: "received_count", pillClass: "received-v2", spanClass: "received-span-v2" }
      ];
    }
  },
  watch: {
    getOverDuesV2(obj) {
      if (obj) {
        this.overDue = obj;
      }
    },
    getKitAvgV2(obj) {
      if (obj) {
        this.kitAvg = obj;
      }
    }
  },
  methods: {
    ...mapActions(["overDuesV2", "setStatusFilterV2", "sendSearchReqV2"]),
    avgDays(val) {
      if (!val) return "0 Days 0 Hours";
      return val;
    },
    ShowOutlinerKits(statusCode, value) {
      this.$emit("showoutlinerkits", { steps: statusCode, value: value });
    },
    formatDate(date) {
      if (!date) return "00/00/0000";
      const formatted = moment(date).format("MM/DD/YY HH:mm");
      return formatted === "Invalid date" ? "00/00/0000" : formatted;
    }
  },
  mounted() {
    const stored = this.$store.getters.getOverDuesV2;
    if (stored && stored.total !== undefined) {
      this.overDue = stored;
    }
    const storedAvg = this.$store.getters.getKitAvgV2;
    if (storedAvg) {
      this.kitAvg = storedAvg;
    }
  }
};
</script>

<style lang="scss" scoped>
@import "../../styles/colors";

.duration-timeline-v2 {
  width: 100%;
  padding-top: 6px;
}

/* ── Outliers count: its own row, fully independent of the transition row so it
   can never shift or misalign the pills below. ── */
.duration-outliers-row-v2 {
  padding: 0 0 6px;
  text-align: left;

  .count-number-v2 {
    font-size: 0.875rem;
    font-family: TTNormsMedium, sans-serif;
    color: #000;
  }

  .count-label-v2 {
    font-size: 0.875rem;
    padding-left: 4px;
    font-family: TTNormsMedium, sans-serif;
    color: #000;
  }
}

/* Scroll wrapper — on mobile this mirrors the funnel's horizontal scroll so the
   transition row keeps the same proportions as the (scrollable) funnel above. */
.duration-pills-scroll-v2 {
  width: 100%;
}

/*
 * One equal-width pill per stage transition, bracketed by two half-width spacers.
 * With 6 pills + two 0.5 spacers the row behaves like 7 equal funnel cells, so
 * pill i centers at (i + 1) / 7 of the row width — exactly the midpoint between
 * funnel node i and node i+1 above — independent of pill text length, count
 * values, or total row width. No flex `gap` is used (a gap would push the slot
 * centers off the node midpoints); spacing lives inside each pill instead.
 */
.duration-pills-v2 {
  display: flex;
  width: 100%;
  align-items: flex-start;
}

.pill-spacer-v2 {
  flex: 0.5 1 0;
  min-width: 0;
}

.duration-pill-v2 {
  flex: 1 1 0;
  min-width: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  cursor: pointer;
  padding: 0 6px;
  box-sizing: border-box;
  transition: transform 0.15s ease;
}

.duration-pill-v2:hover {
  transform: translateY(-2px);
}

/* Average-duration badge — centered in the slot, capped to the slot width */
.stage-pill-v2 {
  display: inline-block;
  max-width: 100%;
  padding: 4px 8px;
  border-radius: 100px;
  font-size: 0.7rem;
  white-space: nowrap;
  color: #fff;
  font-family: TTNormsMedium, sans-serif;
  box-sizing: border-box;
}

/* Transition label — directly below the badge */
.pill-stage-label-v2 {
  margin-top: 4px;
  font-size: 0.65rem;
  font-family: TTNormsMedium, sans-serif;
  color: #6b7280;
  white-space: normal;
  line-height: 1.2;
}

/* Transition count — directly below the label */
.pill-outlier-count-v2 {
  margin-top: 3px;
  font-size: 0.875rem;
  font-weight: 500;
  font-family: TTNormsMedium, sans-serif;
}

/* Pill colours — match the StateV2 funnel stage colours */
.shipping-v2  { background-color: #005EB8; }
.activation-v2 { background-color: #F1B434; }
.awaiting-v2  { background-color: #AC75BD; }
.received-v2  { background-color: #00BBDC; }
.accepted-v2  { background-color: #0EA5E9; }
.dna-v2       { background-color: #17A2B8; }
.sequencing-v2 { background-color: #6F42C1; }
.report-v2    { background-color: #F59E0B; }

/* Outlier count colours */
span.shipping-span-v2  { color: #005EB8; }
span.activation-span-v2 { color: #F1B434; }
span.awaiting-span-v2  { color: #AC75BD; }
span.received-span-v2  { color: #00BBDC; }
span.accepted-span-v2  { color: #0EA5E9; }
span.dna-span-v2       { color: #17A2B8; }
span.sequencing-span-v2 { color: #6F42C1; }
span.report-span-v2    { color: #F59E0B; }

/* Last run date */
.last-run-date-v2 {
  color: #666;
  font-size: 0.75rem;
  font-family: TTNormsMedium, sans-serif;
  padding-top: 6px;
}

/* ── Responsive ── */
@media (max-width: 73.5em) {
  .stage-pill-v2 {
    font-size: 0.65rem;
    padding: 3px 6px;
  }

  .pill-stage-label-v2 {
    font-size: 0.6rem;
    white-space: normal;
  }
}

@media (max-width: 768px) {
  /* Mirror the funnel's mobile behaviour: give the transition row the same
     min-width and let it scroll horizontally, so the spacers + equal pills keep
     the same proportional alignment as the scrollable funnel above. */
  .duration-pills-scroll-v2 {
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
  }

  .duration-pills-v2 {
    min-width: 720px;
  }

  .stage-pill-v2 {
    font-size: 0.6rem;
    padding: 3px 4px;
  }

  .pill-outlier-count-v2 {
    font-size: 0.75rem;
  }

  .pill-stage-label-v2 {
    font-size: 0.55rem;
    white-space: normal;
  }

  .duration-outliers-row-v2 {
    .count-number-v2,
    .count-label-v2 {
      font-size: 0.75rem;
    }
  }
}
</style>
